import { formatDateISO } from '../../utils/dateUtils';
// Cómo se reparte la lista de Trabajos (27-oct-2026).
// El Inicio es el día de hoy; Trabajos es todo lo que no es hoy, en pestañas por lo que hay que hacer:
//   Por agendar · Agendados (de mañana en adelante, más lo atrasado) · Por cobrar · (Cobrados)
// Lo de hoy no aparece en ninguna pestaña (una línea arriba lleva al Inicio), salvo al buscar o al
// llegar desde un número de "Para resolver" del Inicio (filtro), que muestran todo lo que coincide.

export const GRUPOS = [
    { id: 'agendar',   label: 'Por agendar', etapas: ['AGENDAR', 'PRESUPUESTO', 'ASIGNADO'], color: 'var(--etapa-hacer)' },
    { id: 'agendados', label: 'Agendados',   etapas: ['ASIGNADO', 'CAMINO', 'LUGAR'],        color: 'var(--etapa-marcha)' },
    { id: 'cobrar',    label: 'Por cobrar',  etapas: ['HECHO', 'FACTURADO'],                 color: 'var(--etapa-cobrar)' },
    { id: 'cobrado',   label: 'Cobrados',    etapas: ['COBRADO'],                            color: 'var(--etapa-listo)' },
];

/** En qué pestaña va una fila. 'hoy' = está en el Inicio. */
export function grupoDe(f, hoy) {
    if (['AGENDAR', 'PRESUPUESTO'].includes(f.etapa)) return 'agendar';
    if (['ASIGNADO', 'CAMINO', 'LUGAR'].includes(f.etapa)) {
        // 28-oct-2026: fecha tentativa (el cliente todavía no confirmó) → queda en Por agendar, con su técnico y
        // día a la vista. Antes iba a Agendados mezclada con todo y había que buscarla por día y técnico.
        if (tentativa(f)) return 'agendar';
        if (f.coordinar || !f.dia) return f.accion === 'atrasada' ? 'agendados' : 'agendar';
        if (f.accion === 'atrasada') return 'agendados';
        return f.dia === hoy ? 'hoy' : 'agendados';
    }
    if (['HECHO', 'FACTURADO'].includes(f.etapa)) return 'cobrar';
    if (f.etapa === 'COBRADO') return 'cobrado';
    return null;
}

/** Visita atrasada (28-oct-2026): el mismo criterio en el Inicio y en Trabajos, así el número coincide.
 * · abierta (agendada, en camino o en el lugar) de un día anterior, o
 * · "no atendida" que no viene de un pedido de empresa (esas vuelven a Por agendar como pedido). */
export const visitaAtrasada = (o, hoy) => !!o && (
    (['PENDIENTE', 'EN_CAMINO', 'EN_SITIO'].includes(o.estado) && !o.horarioACoordinar && !!o.fechaProgramada && String(o.fechaProgramada).slice(0, 10) < hoy)
    || (o.estado === 'NO_ATENDIDO' && !o.pedidoId));

/** Visita con fecha tentativa: el cliente todavía no confirmó. */
export const tentativa = (f) => f.etapa === 'ASIGNADO' && !!f.orden?.sinConfirmar;

const diaDe = (iso, n) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + n); return formatDateISO(d); };

// Los números de "Para resolver hoy" del Inicio: cada uno abre Trabajos con esto
export const FILTROS = {
    urgentes:      { label: 'Urgentes sin agendar', es: (f) => f.etapa === 'AGENDAR' && f.alerta },
    'por-agendar': { label: 'Pedidos sin agendar',  es: (f) => f.etapa === 'AGENDAR' },
    'sin-ok':      { label: 'Sin "ok, voy" (hoy y mañana)', es: (f, hoy) => {
        const o = f.orden;
        return !!o && o.estado === 'PENDIENTE' && !o.confirmadaEn && !o.sinConfirmar && !o.horarioACoordinar && !o.noPuedeEn
            && [hoy, diaDe(hoy, 1)].includes(String(o.fechaProgramada || '').slice(0, 10));
    } },
    'no-puede':    { label: 'El técnico no puede ir', es: (f) => !!f.orden && f.orden.estado === 'PENDIENTE' && !!f.orden.noPuedeEn },
    atrasadas:     { label: 'Visitas atrasadas', es: (f) => f.accion === 'atrasada' },
    presupuestos:  { label: 'Presupuestos sin respuesta', es: (f) => f.etapa === 'PRESUPUESTO' || tentativa(f) },
};

const porDia = (a, b) => String(a.dia).localeCompare(String(b.dia)) || String(a.fechaOrden).localeCompare(String(b.fechaOrden));

/** Bloques de cada pestaña (null = lista sin bloques). */
export function bloquesDe(grupo, filas, hoy) {
    if (grupo === 'agendar') {
        const urgentes = filas.filter(f => f.etapa === 'AGENDAR' && f.alerta);
        const tentativas = filas.filter(f => tentativa(f)).sort(porDia);
        // Los presupuestos, lo último que se hizo arriba (el que acabás de crear está primero)
        const presupuestos = filas.filter(f => f.etapa === 'PRESUPUESTO')
            .sort((a, b) => String(b.fechaOrden).localeCompare(String(a.fechaOrden)) || String(b.key).localeCompare(String(a.key), undefined, { numeric: true }));
        const coordinar = filas.filter(f => f.coordinar && !tentativa(f));
        const resto = filas.filter(f => ![urgentes, tentativas, presupuestos, coordinar].some(l => l.includes(f)));
        return [['Urgente', urgentes], ['Falta que confirme el cliente', tentativas], ['Presupuestos', presupuestos],
            ['Para agendar', resto], ['A coordinar', coordinar]].filter(([, l]) => l.length);
    }
    if (grupo === 'agendados') {
        const maniana = diaDe(hoy, 1);
        return [
            ['Atrasadas', filas.filter(f => f.accion === 'atrasada').sort(porDia)],
            ['Mañana', filas.filter(f => f.accion !== 'atrasada' && f.dia === maniana).sort(porDia)],
            ['Próximos días', filas.filter(f => f.accion !== 'atrasada' && f.dia && f.dia > maniana).sort(porDia)],
        ].filter(([, l]) => l.length);
    }
    return null;
}
